import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { api, formatApiError, deviceId, authToken, setAuthToken, clearAuthToken, noteSignOut,
         noteAuthFailure, migrateLegacyAuth } from './api';
import { clearAllTripQueues } from './tripQueue';
import { stopNativeTracker } from './nativeTracker';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, {} = logged in
  const [loading, setLoading] = useState(true);

  // Ask the server who we are. The session cookie is http-only and invisible to
  // this code, so the only safe answer is to ask — and a blip on the way must never
  // be mistaken for "signed out".
  // Bumped on every sign-in/out so a slow /auth/me from before cannot undo it.
  const authGen = useRef(0);
  const refresh = useCallback(async () => {
    const gen = authGen.current;
    const attempt = async () => {
      const res = await api.get('/auth/me');
      if (gen !== authGen.current) throw Object.assign(new Error('stale'), { stale: true });
      return res;
    };
    try {
      await migrateLegacyAuth().catch(() => {});
      const { data } = await attempt();
      setUser(data);
    } catch (e) {
      if (e.stale || gen !== authGen.current) return;
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      noteAuthFailure(status, '/auth/me', detail || e.message);
      // A 403 that does not come from our own API (a Cloudflare/WAF challenge, an
      // office firewall, an extension) is not a signed-out user — it is a blocked
      // request. Keep the sign-in and say so, instead of a silent trip to the form.
      if (status === 403 && !detail) {
        noteSignOut('blocked');
        setUser(false);
        setLoading(false);
        return;
      }
      if (!status || status >= 500 || status === 429) {
        // Network dropped or the server hiccuped — try once more before giving up.
        await new Promise(r => setTimeout(r, 1500));
        try {
          const { data } = await attempt();
          setUser(data);
          setLoading(false);
          return;
        } catch (again) {
          if (again.stale || gen !== authGen.current) return;
          const retryStatus = again.response?.status;
          if (!retryStatus || retryStatus >= 500 || retryStatus === 429) {
            noteSignOut('offline');
            setUser(false);
            setLoading(false);
            return;
          }
        }
      }
      if (status === 401) noteSignOut('expired');
      clearAuthToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password, device_id: deviceId() });
      if (data.otp_required) {
        return { ok: false, otpRequired: true, challenge: data.challenge, emailHint: data.email_hint,
                 methods: data.methods || ['email'], emailSent: !!data.email_sent };
      }
      authGen.current += 1;
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const verifyOtp = useCallback(async (challenge, code, method = 'email') => {
    const path = method === 'totp' ? '/auth/verify-totp'
      : method === 'recovery' ? '/auth/verify-recovery-code' : '/auth/verify-otp';
    try {
      const { data } = await api.post(path, { challenge, code });
      authGen.current += 1;
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const resendOtp = useCallback(async (challenge) => {
    try {
      const { data } = await api.post('/auth/resend-otp', { challenge });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    await stopNativeTracker();
    try { await api.post('/auth/logout'); }
    catch (e) { console.warn('logout request failed, clearing session locally', e); }
    clearAuthToken();
    clearAllTripQueues();
    noteSignOut('self');
    setUser(false);
  }, []);

  // Ends an "open as user" look-in. Only that session dies — the real user keeps
  // working, and the Super Admin's own tabs are untouched.
  const stopImpersonation = useCallback(async () => {
    try { await api.post('/auth/impersonate/stop'); }
    catch (e) { console.warn('could not end the look-in cleanly', e); }
    try { sessionStorage.removeItem('sahakar_imp_token'); } catch { /* storage blocked */ }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, setUser, verifyOtp, resendOtp, stopImpersonation }),
    [user, loading, login, logout, refresh, verifyOtp, resendOtp, stopImpersonation],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
